import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchApplicationProducts } from '../services/ApplicationProductsService';
import { useProducts } from './useProducts';
import { useSubscriptionStatus } from './useSubscriptionStatus';
import { Product } from '../types';
import { fetch as fetchApplication } from '../../../accounts-management/applications/services/ApplicationsService';
import { fetchPricesByProduct } from '../../prices/services/PricesService';

export const useApplicationDetail = (applicationId: string) => {
    const {
        data: application,
        isLoading: applicationLoading,
    } = useQuery({
        queryKey: ['application', applicationId],
        queryFn: () => fetchApplication(applicationId),
        enabled: !!applicationId,
    });

    const {
        data: applicationProducts,
        isLoading: mappingsLoading,
    } = useQuery({
        queryKey: ['applicationProducts', applicationId],
        queryFn: () => fetchApplicationProducts(applicationId),
        enabled: !!applicationId,
    });

    const { products: allProducts, isLoading: productsLoading } = useProducts();
    const { statusByProductId, loading: subscriptionsLoading } = useSubscriptionStatus();

    // Build product list for this application with full price data
    const products = useMemo<Product[]>(() => {
        if (!applicationProducts || !allProducts) return [];

        const productMap = new Map(allProducts.map(p => [p.id, p]));

        return applicationProducts
            .filter(ap => ap.applicationProductStatus !== 'INACTIVE')
            .sort((a, b) => (a.position ?? 999) - (b.position ?? 999))
            .map(ap => productMap.get(ap.productId))
            .filter((p): p is Product => !!p && p.status === 'Active');
    }, [applicationProducts, allProducts]);

    // Fetch prices for each product in this application
    const {
        data: pricesByProduct,
        isLoading: pricesLoading,
    } = useQuery({
        queryKey: ['applicationPrices', applicationId, products.map(p => p.id)],
        queryFn: async () => {
            const entries = await Promise.all(
                products.map(async (p) => {
                    const prices = await fetchPricesByProduct(p.id);
                    return [p.id, prices] as const;
                })
            );
            return Object.fromEntries(entries) as Record<string, typeof entries[0][1]>;
        },
        enabled: products.length > 0,
    });

    // Enrich products with fetched prices
    const productsWithPrices = useMemo<Product[]>(() => {
        if (!pricesByProduct) return products;
        return products.map(p => ({
            ...p,
            prices: {
                items: pricesByProduct[p.id] || p.prices?.items || [],
                nextToken: null,
            },
        }));
    }, [products, pricesByProduct]);

    const isLoading = applicationLoading || mappingsLoading || productsLoading || subscriptionsLoading || pricesLoading;

    return {
        application,
        products: productsWithPrices,
        statusByProductId,
        isLoading,
    };
};
